import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useExtraction } from "../../../hooks/useExtraction";
import ExtractionTable from "../../../components/extractionViewer/ExtractionTable";
import { ExtractionValueGroup } from "../../../components/extractionViewer/ExtractionValueGroup";
import Divider from "../../../components/templateManager/Divider";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import { Button } from "../../../components/ui/button";
import { fetchFastApi } from "../../../api/fastapi";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { pdfDocument } from "../../../models/extraction.ts";
import { BoundingBox } from "./ExtractionFileViewer.tsx";
import _ from "lodash";
import ResizableSplitView from "./ResizeableSplitView.tsx";
import ExtractionFileViewer from "./ExtractionFileViewer.tsx";

export const ViewExtractionPage = () => {
  const { extractionId } = useParams();
  const { attributes, extraction, loading, outputMappings, signedUrls } =
    useExtraction({
      extractionId: extractionId || "",
      fetchOnLoad: true,
    });
  const [downloading, setDownloading] = useState(false);
  const [boundingBox, setBoundingBox] = useState<{
    boundingBox?: BoundingBox;
    pageWidth?: number;
    pageHeight?: number;
    initialPage?: number;
  } | null>(null);
  const [currentSelectedElementId, setCurrentSelectedElementId] = useState<
    string | null
  >(null);

  const processOutputName = (input: string) => {
    // Replace whitespace with underscores
    return _.replace(input, /\s+/g, "_");
  };

  const handleDownloadExtraction = useCallback(
    async (outputMappingId?: string, outputMappingName?: string) => {
      if (!extractionId) return;

      setDownloading(true);
      try {
        const query = outputMappingId
          ? `?output_mapping_id=${outputMappingId}`
          : "";
        const response = await fetchFastApi({
          path: `/extractions/extraction/${extractionId}/download/excel`,
          method: "GET",
          query,
        });

        const url = window.URL.createObjectURL(response);
        const a = document.createElement("a");
        a.href = url;
        const downloadName = processOutputName(
          outputMappingName ?? "midship_extraction",
        );
        a.download = `${downloadName}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading the file:", error);
        toast.error("Error downloading the file");
      }
      setDownloading(false);
    },
    [extractionId],
  );

  // Update the bounding box and PDF page number when a new source element is selected
  const handleNewSourceElementId = (newSourceElementId: string) => {
    console.log("New source element ID:", newSourceElementId);
    if (extraction && extraction.length > 0) {
      const parsedDoc = extraction[0].parsedDoc;
      if (parsedDoc) {
        if (pdfDocument.safeParse(parsedDoc.document).success) {
          const pdfDoc = pdfDocument.safeParse(parsedDoc.document)!.data;
          const element = pdfDoc?.elements.find(
            (elem) => elem.id === newSourceElementId,
          );
          if (!element) {
            console.error("Element not found in PDF document");
            return;
          }
          setCurrentSelectedElementId(newSourceElementId);

          const newPage = element.startPageNumber;

          const width = pdfDoc?.widths[newPage - 1];
          const height = pdfDoc?.heights[newPage - 1];

          setBoundingBox({
            boundingBox: element.boundingBox ?? undefined,
            pageWidth: width,
            pageHeight: height,
            initialPage: newPage,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (!extraction || extraction.length === 0 || !currentSelectedElementId)
      return;

    const parsedDoc = extraction[0].parsedDoc;
    if (parsedDoc) {
      if (pdfDocument.safeParse(parsedDoc.document).success) {
        const pdfDoc = pdfDocument.safeParse(parsedDoc.document)!.data;
        const element = pdfDoc?.elements.find(
          (elem) => elem.id === currentSelectedElementId,
        );
        if (!element) {
          console.error("Element not found in PDF document");
          return;
        }

        const width = pdfDoc?.widths[element.startPageNumber - 1];
        const height = pdfDoc?.heights[element.startPageNumber - 1];

        // Update page dimensions whenever the selected element changes
        setBoundingBox({
          boundingBox: element.boundingBox ?? undefined,
          pageWidth: width,
          pageHeight: height,
          initialPage: element.startPageNumber,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedElementId]);

  const tableContent = (
    <div className="flex flex-col w-full">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-semibold">View Extraction</h1>
        {outputMappings.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button loading={downloading} variant="outline">
                Download
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleDownloadExtraction()}>
                Blank Excel
              </DropdownMenuItem>
              {outputMappings.map((outputMapping) => (
                <DropdownMenuItem
                  key={outputMapping.id}
                  onClick={() =>
                    handleDownloadExtraction(
                      outputMapping.id,
                      outputMapping.name,
                    )
                  }
                >
                  {outputMapping.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            loading={downloading}
            variant="outline"
            onClick={() => handleDownloadExtraction()}
          >
            Download
          </Button>
        )}
      </div>
      {loading ? (
        <div className="flex justify-center items-center mt-10">
          <LoadingSpinner />
        </div>
      ) : (
        extraction &&
        extraction.map((file) => (
          <div className="flex flex-col gap-8 mt-10" key={file.file.fileName}>
            <h2 className="text-lg font-semibold">{file.file.fileName}</h2>
            {Object.entries(file.data.valueGroups).map(([key, valueGroup]) => (
              <ExtractionValueGroup
                key={key}
                valueGroup={valueGroup}
                attributes={attributes}
                setSourceElementId={handleNewSourceElementId}
              />
            ))}
            <Divider />
            {Object.entries(file.data.tables).map(([key, table]) => (
              <ExtractionTable
                key={key}
                table={table}
                attributes={attributes}
                setSourceElementId={handleNewSourceElementId}
              />
            ))}
          </div>
        ))
      )}
    </div>
  );

  const getFileExtension = _.flow([
    (filename: string) => _.split(filename, "."),
    (parts) => (parts.length > 1 ? _.last(parts) : ""),
  ]);

  const rightContent = (
    <div className="flex flex-col h-full px-4 pt-4">
      {signedUrls && (
        <div className="grow overflow-hidden">
          <ExtractionFileViewer
            signedUrl={Object.entries(signedUrls)[0][1]}
            fileType={getFileExtension(
              extraction?.[0]?.file.fileName ?? ".pdf",
            )}
            initialPage={boundingBox?.initialPage}
            boundingBox={boundingBox?.boundingBox}
            pageWidth={boundingBox?.pageWidth}
            pageHeight={boundingBox?.pageHeight}
          />
        </div>
      )}
    </div>
  );

  if (!loading && (!extraction || extraction.length === 0))
    return (
      <div className="h-full w-full overflow-auto">
        <div className="flex flex-col gap-4 sm:p-20 p-6">
          <h1 className="text-2xl font-semibold">Extraction not found</h1>
          <p className="text-sm text-gray-500">
            The extraction you are looking for does not exist. This may be
            because your workspace has zero data retention enabled.
          </p>
        </div>
      </div>
    );

  return (
    <div className="h-screen w-full overflow-hidden">
      {getFileExtension(extraction?.[0]?.file.fileName ?? ".pdf") === "pdf" ? (
        <ResizableSplitView
          tableContent={tableContent}
          rightContent={rightContent}
        />
      ) : (
        <div className="h-full w-full p-6 overflow-auto">{tableContent}</div>
      )}
    </div>
  );
};
